import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment';
import { GetBeacon4SidePolygons, GetBeacon4PerformanceFilters } from '../requests/Beacon4PerformanceRequests';

// Blended's Side/Polygon/JV ID lookup — unscoped, global list from
// All_Cellsites_Foresight_Unique. Unaffected by the Performance rework below.
export const loadBeacon4SidePolygons = createAsyncThunk(
	'beacon4Filters/loadSidePolygons',
	async (_, { rejectWithValue }) => {
		try {
			const res = await GetBeacon4SidePolygons();
			return res?.data ?? res;
		} catch (e) {
			return rejectWithValue(e.response?.data ?? e.message);
		}
	}
);

// Performance's Side/Polygon/Vendor/JV ID lookup — scoped to the selected
// Tech+KPI's source view and date range (Beacon4Performance_filters_data).
// Dispatched whenever Tech/KPI or the date range changes (see Beacon4FilterBar).
export const loadBeacon4PerformanceFilters = createAsyncThunk(
	'beacon4Filters/loadPerformanceFilters',
	async ({ tech, mainKpiKey, dateFrom, dateTo }, { rejectWithValue }) => {
		try {
			const res = await GetBeacon4PerformanceFilters({ tech, mainKpiKey, dateFrom, dateTo });
			return res?.data ?? res;
		} catch (e) {
			return rejectWithValue(e.response?.data ?? e.message);
		}
	}
);

// Shared cascade helper for Performance's Side > Polygon > Vendor > JV ID
// hierarchy. Unlike Blended's allPairs (huge, global, computed once), perf
// pairs are already tightly scoped to one Tech+KPI+date range, so there's no
// need for the reverse-derivation JV-ID-picked-first logic Blended's setters
// have — picking a JV ID directly just doesn't need to narrow Side/Polygon/
// Vendor in a set this small.
function computePerfCascade(pairs, { side, polygon, vendor }) {
	const selSides    = side    ? side.split(',').map(v => v.trim()).filter(Boolean)    : [];
	const selPolygons = polygon ? polygon.split(',').map(v => v.trim()).filter(Boolean) : [];
	const selVendors  = vendor  ? vendor.split(',').map(v => v.trim()).filter(Boolean)  : [];

	const bySide    = pairs.filter(p => selSides.length === 0 || selSides.includes(p.side));
	const byPolygon = bySide.filter(p => selPolygons.length === 0 || selPolygons.includes(p.polygon));
	const byVendor  = byPolygon.filter(p => selVendors.length === 0 || selVendors.includes(p.vendor));

	return {
		sides:    [...new Set(pairs.map(p => p.side))].sort(),
		polygons: [...new Set(bySide.map(p => p.polygon))].sort(),
		vendors:  [...new Set(byPolygon.map(p => p.vendor).filter(Boolean))].sort(),
		jvids:    [...new Set(byVendor.map(p => p.jvId).filter(Boolean))].sort(),
	};
}

// Given a comma-separated selection and the list of values still valid for
// the new source, keeps whichever selected values survive and reports
// whichever don't — used on refetch to preserve as much of the user's
// existing filter selection as possible instead of wiping it outright.
function keepValidValues(selectedStr, validValues) {
	if (!selectedStr) return { kept: '', removed: [] };
	const validSet = new Set(validValues);
	const selected = selectedStr.split(',').map(v => v.trim()).filter(Boolean);
	return {
		kept:    selected.filter(v => validSet.has(v)).join(','),
		removed: selected.filter(v => !validSet.has(v)),
	};
}

const beacon4FiltersSlice = createSlice({
	name: 'beacon4Filters',
	initialState: {
		module: 'Blended',

		dateFrom:    moment().subtract(1, 'months').format('YYYY-MM-DD'),
		dateTo:      moment().format('YYYY-MM-DD'),
		aggregation: 'Daily',

		// Blended's Side/Polygon/JV ID selections (unscoped, global list).
		side:        '',
		polygon:     '',
		jvid:        '',

		perfTech:            '2G',
		perfMainKpi:         '',
		perfTableKpi:        '',   // KPI shown in the aggregation table; defaults to Main KPI on Apply, then independent
		perfAggregationMode: 'Site',
		adminDialogOpen:     false,
		// Legend panel visibility — shared across both modules (not kept
		// per-module the way the legend's resizable WIDTH is in App.jsx,
		// since there's no indication the user would want it shown in one
		// module and hidden in the other). Defaults to visible.
		legendVisible:       true,

		// Performance's own Side/Polygon/Vendor/JV ID selections — kept
		// separate from Blended's above since they're sourced completely
		// differently (scoped to Tech+KPI+date range, not global).
		perfSide:    '',
		perfPolygon: '',
		perfVendor:  '',
		perfJvid:    '',
		perfFilterPairs:    [],   // raw pairs for the current Tech+KPI+date scope
		perfFiltersViewName: '',  // last-seen source view (informational only now)
		perfSides:    [],
		perfPolygons: [],
		perfVendors:  [],
		perfJvids:    [],
		perfFiltersLoading: false,
		perfFiltersError:   null,
		// Values dropped on the last refetch because they no longer exist in
		// the new source — e.g. [{ field: 'Polygon', values: ['POLY_12'] }].
		// Drives a dismissible notice in the FilterBar so a selection change
		// mid-analysis doesn't look like a silent bug.
		perfFiltersRemoved: [],

		allPairs: [],
		sides:    [],
		polygons: [],
		allJvids: [],
		jvids:    [],

		visibleSeries: null,
		visibleSeriesLeft:  null,
		visibleSeriesRight: null,
		loading:       false,
		error:         null,
	},
	reducers: {
		setBeacon4Module(state, action) {
			state.module             = action.payload;
			// Reset visibility when switching modules so leftover toggles don't
			// silently filter charts in the other module.
			state.visibleSeries      = null;
			state.visibleSeriesLeft  = null;
			state.visibleSeriesRight = null;
		},

		// `action.payload` is `{ tech, availableKeys }`, where availableKeys is
		// every KPI key (Main + Breakdown) that exists for the tech being
		// switched to — computed by the caller from perfKpisByTech, since this
		// slice doesn't otherwise know about the KPI catalogue. Kept as an
		// object (rather than plain `tech` string) specifically so both
		// call sites can supply that list and get the same preserve-if-valid
		// treatment, instead of one of them always hard-resetting.
		setBeacon4PerfTech(state, action) {
			const { tech, availableKeys = [] } = action.payload;
			state.perfTech = tech;

			// Many KPIs (especially Main KPIs like AVAIL) exist across every
			// Tech, so switching Tech doesn't always mean the current KPI
			// stops making sense — only reset it (and, as a consequence, the
			// whole filter cascade below it) when it genuinely isn't offered
			// for the new Tech. This mirrors the same principle as the
			// Side/Polygon/Vendor/JV ID preservation on view change: don't
			// throw away a selection just because something upstream of it
			// changed, if it's still valid.
			const keepMainKpi  = !!state.perfMainKpi  && availableKeys.includes(state.perfMainKpi);
			const keepTableKpi = !!state.perfTableKpi && availableKeys.includes(state.perfTableKpi);

			if (!keepMainKpi) {
				state.perfMainKpi = '';
				// No Main KPI ⇒ no source view to scope the filter cascade to,
				// so it has to be cleared regardless of whether individual
				// Side/Polygon/Vendor/JV ID values would otherwise still be
				// valid — there's nothing left to validate them against.
				state.perfFiltersViewName = '';
				state.perfFilterPairs = [];
				state.perfSide = ''; state.perfPolygon = ''; state.perfVendor = ''; state.perfJvid = '';
				state.perfSides = []; state.perfPolygons = []; state.perfVendors = []; state.perfJvids = [];
			}
			// else: Main KPI still valid for the new Tech — leave it and the
			// current filter selections alone. The FilterBar effect refetches
			// Beacon4Performance_filters_data for (tech, mainKpi), and the
			// existing keep-valid cascade in loadBeacon4PerformanceFilters
			// .fulfilled decides what actually survives against that source.

			if (!keepTableKpi) state.perfTableKpi = '';
		},
		setBeacon4PerfMainKpi(state, action)        { state.perfMainKpi         = action.payload; },
		setBeacon4PerfTableKpi(state, action)       { state.perfTableKpi        = action.payload; },
		setBeacon4PerfAggregationMode(state, action){ state.perfAggregationMode = action.payload; },
		setBeacon4AdminDialog(state, action) { state.adminDialogOpen = action.payload; },
		toggleBeacon4Legend(state) { state.legendVisible = !state.legendVisible; },

		setBeacon4Aggregation(state, action) { state.aggregation = action.payload; },
		setBeacon4DateFrom(state, action)    { state.dateFrom    = action.payload; },
		setBeacon4DateTo(state, action)      { state.dateTo      = action.payload; },

		// ── Blended: Side/Polygon/JV ID (unchanged, global allPairs) ─────────
		setBeacon4Side(state, action) {
			state.side    = action.payload;
			state.polygon = '';
			state.jvid    = '';
			const selectedSides = action.payload
				? action.payload.split(',').map(v => v.trim()).filter(Boolean)
				: [];
			state.polygons = [...new Set(
				state.allPairs
					.filter(p => selectedSides.length === 0 || selectedSides.includes(p.side))
					.map(p => p.polygon)
			)].sort();
			state.jvids = [...new Set(
				state.allPairs
					.filter(p => selectedSides.length === 0 || selectedSides.includes(p.side))
					.map(p => p.jvid ?? p.Jvid)
					.filter(Boolean)
			)].sort();
		},

		setBeacon4Polygon(state, action) {
			state.polygon = action.payload;
			state.jvid    = '';
			const selectedSides    = state.side ? state.side.split(',').map(v => v.trim()).filter(Boolean) : [];
			const selectedPolygons = action.payload ? action.payload.split(',').map(v => v.trim()).filter(Boolean) : [];
			state.jvids = [...new Set(
				state.allPairs
					.filter(p =>
						(selectedSides.length === 0    || selectedSides.includes(p.side)) &&
						(selectedPolygons.length === 0 || selectedPolygons.includes(p.polygon))
					)
					.map(p => p.jvid ?? p.Jvid)
					.filter(Boolean)
			)].sort();
		},

		setBeacon4Jvid(state, action) {
			state.jvid = action.payload;
			const selectedJvids = action.payload
				? action.payload.split(',').map(v => v.trim()).filter(Boolean)
				: [];
			if (selectedJvids.length > 0) {
				const matchingPairs = state.allPairs.filter(p =>
					selectedJvids.includes(p.jvid ?? p.Jvid)
				);
				if (!state.side) {
					state.sides    = [...new Set(matchingPairs.map(p => p.side))].sort();
					state.polygons = [...new Set(matchingPairs.map(p => p.polygon))].sort();
				} else {
					const selectedSides = state.side.split(',').map(v => v.trim()).filter(Boolean);
					state.polygons = [...new Set(
						matchingPairs.filter(p => selectedSides.includes(p.side)).map(p => p.polygon)
					)].sort();
				}
			} else {
				state.sides = [...new Set(state.allPairs.map(p => p.side))].sort();
				if (state.side) {
					const selectedSides = state.side.split(',').map(v => v.trim()).filter(Boolean);
					state.polygons = [...new Set(
						state.allPairs.filter(p => selectedSides.includes(p.side)).map(p => p.polygon)
					)].sort();
				} else {
					state.polygons = [];
				}
			}
		},

		// ── Performance: Side/Polygon/Vendor/JV ID (new, scoped to perfFilterPairs) ──
		setBeacon4PerfSide(state, action) {
			state.perfSide    = action.payload;
			state.perfPolygon = '';
			state.perfVendor  = '';
			state.perfJvid    = '';
			const c = computePerfCascade(state.perfFilterPairs, { side: action.payload, polygon: '', vendor: '' });
			state.perfSides = c.sides; state.perfPolygons = c.polygons;
			state.perfVendors = c.vendors; state.perfJvids = c.jvids;
		},
		setBeacon4PerfPolygon(state, action) {
			state.perfPolygon = action.payload;
			state.perfVendor  = '';
			state.perfJvid    = '';
			const c = computePerfCascade(state.perfFilterPairs, { side: state.perfSide, polygon: action.payload, vendor: '' });
			state.perfSides = c.sides; state.perfPolygons = c.polygons;
			state.perfVendors = c.vendors; state.perfJvids = c.jvids;
		},
		setBeacon4PerfVendor(state, action) {
			state.perfVendor = action.payload;
			state.perfJvid   = '';
			const c = computePerfCascade(state.perfFilterPairs, { side: state.perfSide, polygon: state.perfPolygon, vendor: action.payload });
			state.perfSides = c.sides; state.perfPolygons = c.polygons;
			state.perfVendors = c.vendors; state.perfJvids = c.jvids;
		},
		// JV ID sits at the bottom of the cascade — nothing depends on it, so
		// picking one doesn't need to recompute anything else.
		setBeacon4PerfJvid(state, action) { state.perfJvid = action.payload; },

		// Dismiss the "some filters were removed" notice once the user has
		// seen it. Doesn't touch the filters themselves, just the notice.
		clearBeacon4PerfFiltersRemoved(state) { state.perfFiltersRemoved = []; },

		toggleBeacon4Series(state, action) {
			const key = action.payload;
			if (state.visibleSeries === null) {
				state.visibleSeries = [key];
			} else if (state.visibleSeries.includes(key)) {
				const next = state.visibleSeries.filter(k => k !== key);
				state.visibleSeries = next.length > 0 ? next : null;
			} else {
				state.visibleSeries = [...state.visibleSeries, key];
			}
		},
		// Performance — panel-scoped toggle ('left' = KPI Performance, 'right' = KPI Failures)
		toggleBeacon4PanelSeries(state, action) {
			const { panel, key } = action.payload;
			const field = panel === 'right' ? 'visibleSeriesRight' : 'visibleSeriesLeft';
			const current = state[field];
			if (current === null) {
				state[field] = [key];
			} else if (current.includes(key)) {
				const next = current.filter(k => k !== key);
				state[field] = next.length > 0 ? next : null;
			} else {
				state[field] = [...current, key];
			}
		},
		resetBeacon4HiddenSeries(state) {
			state.visibleSeries      = null;
			state.visibleSeriesLeft  = null;
			state.visibleSeriesRight = null;
		},
	},
	extraReducers: (b) => {
		b.addCase(loadBeacon4SidePolygons.pending, (s) => { s.loading = true; s.error = null; })
		 .addCase(loadBeacon4SidePolygons.fulfilled, (s, a) => {
				s.loading  = false;
				s.allPairs = a.payload ?? [];
				s.sides    = [...new Set((a.payload ?? []).map(p => p.side))].sort();
				s.allJvids = [...new Set((a.payload ?? []).map(p => p.jvid ?? p.Jvid).filter(Boolean))].sort();
				if (s.side) {
					const selectedSides = s.side.split(',').map(v => v.trim()).filter(Boolean);
					s.polygons = [...new Set(
						(a.payload ?? []).filter(p => selectedSides.includes(p.side)).map(p => p.polygon)
					)].sort();
					if (s.polygon) {
						const sel = s.polygon.split(',').map(v => v.trim()).filter(Boolean);
						s.jvids = [...new Set(
							(a.payload ?? [])
								.filter(p => selectedSides.includes(p.side) && (sel.length === 0 || sel.includes(p.polygon)))
								.map(p => p.jvid ?? p.Jvid)
								.filter(Boolean)
						)].sort();
					} else {
						s.jvids = [...new Set(
							(a.payload ?? []).filter(p => selectedSides.includes(p.side)).map(p => p.jvid ?? p.Jvid).filter(Boolean)
						)].sort();
					}
				} else {
					s.jvids = s.allJvids;
				}
			})
		 .addCase(loadBeacon4SidePolygons.rejected, (s, a) => { s.loading = false; s.error = a.payload; })

		 .addCase(loadBeacon4PerformanceFilters.pending, (s) => { s.perfFiltersLoading = true; s.perfFiltersError = null; })
		 .addCase(loadBeacon4PerformanceFilters.fulfilled, (s, a) => {
				s.perfFiltersLoading = false;
				const { viewName, pairs } = a.payload ?? {};
				s.perfFiltersViewName = viewName ?? '';
				s.perfFilterPairs = pairs ?? [];

				// Rather than wiping the whole Side/Polygon/Vendor/JV ID
				// selection whenever the source view changes, validate each
				// level of the cascade against the NEW pairs and keep
				// whatever still exists. Losing filters mid-analysis is
				// disruptive, and different KPIs often still share most of
				// the same Side/Polygon universe even when their source view
				// differs. A value that no longer exists invalidates
				// whatever was chosen below it in the cascade — same as if
				// the user had picked a new value there by hand — so this
				// walks top-down, re-deriving the valid-options list at each
				// step from whatever survived the level above.
				let c = computePerfCascade(s.perfFilterPairs, { side: '', polygon: '', vendor: '' });
				const sideResult = keepValidValues(s.perfSide, c.sides);
				s.perfSide = sideResult.kept;

				c = computePerfCascade(s.perfFilterPairs, { side: s.perfSide, polygon: '', vendor: '' });
				const polygonResult = keepValidValues(s.perfPolygon, c.polygons);
				s.perfPolygon = polygonResult.kept;

				c = computePerfCascade(s.perfFilterPairs, { side: s.perfSide, polygon: s.perfPolygon, vendor: '' });
				const vendorResult = keepValidValues(s.perfVendor, c.vendors);
				s.perfVendor = vendorResult.kept;

				c = computePerfCascade(s.perfFilterPairs, { side: s.perfSide, polygon: s.perfPolygon, vendor: s.perfVendor });
				const jvidResult = keepValidValues(s.perfJvid, c.jvids);
				s.perfJvid = jvidResult.kept;

				s.perfSides = c.sides; s.perfPolygons = c.polygons;
				s.perfVendors = c.vendors; s.perfJvids = c.jvids;

				// Surface exactly what got dropped (if anything) — silently
				// changing the user's selection under them would otherwise
				// look like a bug rather than an expected consequence of the
				// KPI/date range they just changed.
				s.perfFiltersRemoved = [
					sideResult.removed.length    > 0 ? { field: 'Side',    values: sideResult.removed }    : null,
					polygonResult.removed.length > 0 ? { field: 'Polygon', values: polygonResult.removed } : null,
					vendorResult.removed.length  > 0 ? { field: 'Vendor',  values: vendorResult.removed }  : null,
					jvidResult.removed.length    > 0 ? { field: 'JV ID',   values: jvidResult.removed }    : null,
				].filter(Boolean);
			})
		 .addCase(loadBeacon4PerformanceFilters.rejected, (s, a) => { s.perfFiltersLoading = false; s.perfFiltersError = a.payload; });
	},
});

export const {
	setBeacon4Module,
	setBeacon4PerfTech, setBeacon4PerfMainKpi, setBeacon4PerfTableKpi, setBeacon4PerfAggregationMode,
	setBeacon4AdminDialog,
	setBeacon4Aggregation,
	setBeacon4DateFrom, setBeacon4DateTo,
	setBeacon4Side, setBeacon4Polygon, setBeacon4Jvid,
	setBeacon4PerfSide, setBeacon4PerfPolygon, setBeacon4PerfVendor, setBeacon4PerfJvid,
	clearBeacon4PerfFiltersRemoved,
	toggleBeacon4Legend,
	toggleBeacon4Series, toggleBeacon4PanelSeries, resetBeacon4HiddenSeries,
} = beacon4FiltersSlice.actions;

export default beacon4FiltersSlice.reducer;

export const selectBeacon4Filters             = (state) => state.beacon4Filters;
export const selectBeacon4Module              = (state) => state.beacon4Filters.module;
export const selectBeacon4VisibleSeries       = (state) => state.beacon4Filters.visibleSeries;
export const selectBeacon4AllJvids            = (state) => state.beacon4Filters.allJvids;
export const selectBeacon4PerfTech            = (state) => state.beacon4Filters.perfTech;
export const selectBeacon4PerfMainKpi         = (state) => state.beacon4Filters.perfMainKpi;
export const selectBeacon4PerfTableKpi        = (state) => state.beacon4Filters.perfTableKpi;
export const selectBeacon4PerfAggregationMode = (state) => state.beacon4Filters.perfAggregationMode;
export const selectBeacon4AdminDialogOpen = (state) => state.beacon4Filters.adminDialogOpen;
export const selectBeacon4LegendVisible   = (state) => state.beacon4Filters.legendVisible;
export const selectBeacon4VisibleSeriesLeft  = (state) => state.beacon4Filters.visibleSeriesLeft;
export const selectBeacon4VisibleSeriesRight = (state) => state.beacon4Filters.visibleSeriesRight;
export const selectBeacon4PerfFiltersRemoved = (state) => state.beacon4Filters.perfFiltersRemoved;